/* eslint-disable no-confusing-arrow */
import React from 'react';
import _has from 'lodash/has';
import { connect, ConnectedProps } from 'react-redux';
import {
  Action,
  ConfigService,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { InlineError } from '@piwikpro/platform/components/Errors';
import {
  Information,
  Stack,
  withTranslation,
  TFunction,
  HelpLink,
  Divider,
  Skeleton,
  Tag,
  Icon,
  Tooltip,
  Text,
  formatDate,
} from '@piwikpro/ui-components';
import { MetaSites } from '@piwikpro/meta-sites-crate';
import { MetaSitesState } from '@piwikpro/meta-sites-crate/reducers';
import { Timezone } from '@piwikpro/platform/config/timeZones';

const mapStateToProps = (state: MetaSitesState) => ({
  metaSite: state.metaSites.details.settings.settings,
  currencyValid: state.metaSites.details.settings.settings.currencyValid,
  timezoneValid: state.metaSites.details.settings.settings.timezoneValid,
  insideApps: state.metaSites.apps.inside.list,
  isLoadingInsideApps: state.metaSites.apps.inside.isLoading,
  failedInsideApps: state.metaSites.apps.inside.failed,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  metaSites: MetaSites
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

interface State {
  timezoneSearch: string
}

class GeneralTimezoneAndCurrency extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      timezoneSearch: '',
    };

    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.clearSearchInput = this.clearSearchInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  getAvailableTimezones(searchValue, items) {
    const { metaSite } = this.props;

    return items.filter(
      item => item.id !== metaSite.attributes.timezone
        ? this.searchValueMatchesItem(searchValue, item)
        : item,
    );
  }

  getAvailableCurrencies(searchValue, items) {
    const { metaSite } = this.props;

    return items.filter(
      item => item.id !== metaSite.attributes.currency
        ? this.searchValueMatchesItem(searchValue, item)
        : item,
    );
  }

  searchValueMatchesItem(searchValue, item) {
    return _has(item, 'name')
      && item.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
      && item.name.length >= searchValue.length;
  }

  handleSearchChange(e) {
    this.setState({
      timezoneSearch: e.target.value,
    });
  }

  async handleSubmit(values, { setErrors, setSubmitting, resetForm }) {
    const { metaSites, metaSite } = this.props;

    try {
      await metaSites.updateMetaSite(metaSite.id, values);
      await metaSites.fetchMetaSiteIntegrity(metaSite.id);
    } catch (err: any) {
      setErrors(err.errors);
      resetForm();
    }

    setSubmitting(false);
  }

  clearSearchInput() {
    this.setState({
      timezoneSearch: '',
    });
  }

  // eslint-disable-next-line consistent-return
  getCurrencyStatus() {
    const {
      currencyValid,
      insideApps,
      isLoadingInsideApps,
      failedInsideApps,
      t,
    } = this.props;

    if (failedInsideApps) {
      return <InlineError />;
    }

    if (isLoadingInsideApps) {
      return <Skeleton width={40} height={25} />;
    }

    if (!isLoadingInsideApps && !insideApps.length) {
      return (
        <Tooltip
          position="left"
          content={t('meta-sites.details.settings.general.currency.no-applicable-currencies-tooltip')}
        >
          <Tag>{t('general.no-applicable')}</Tag>
        </Tooltip>
      );
    }

    if (currencyValid && insideApps.length) {
      return (
        <Tooltip
          position="left"
          content={t('meta-sites.details.settings.general.currency.valid-currencies-tooltip')}
        >
          <Tag color="blue">{insideApps[0].attributes.currency}</Tag>
        </Tooltip>
      );
    }

    if (!currencyValid) {
      return (
        <Tooltip
          position="left"
          content={t('meta-sites.details.settings.general.currency.multiple-currencies-tooltip')}
        >
          <Stack spacing="narrow" verticalAlignment="center">
            <Stack.Item>
              <Icon name="exclamation-mark-circle" color="yellow" />
            </Stack.Item>
            <Stack.Item>
              <Tag color="yellow">
                {t('meta-sites.details.settings.general.currency.multiple-currencies')}
              </Tag>
            </Stack.Item>
          </Stack>
        </Tooltip>
      );
    }
  }

  render() {
    const {
      metaSite,
      timezoneValid,
      config,
      t,
    } = this.props;
    const { timezoneSearch } = this.state;
    const { timezone } = metaSite.attributes;
    const filteredTimezones = this.getAvailableTimezones(timezoneSearch, config.get('AVAILABLE_TIME_ZONES'));
    const timezoneOption = (config.get('AVAILABLE_TIME_ZONES') as Timezone)
      .find(option => option.id === timezone);

    return (
      <Stack vertical horizontalAlignment="middle">
        <Stack.Item fill>
          <Stack vertical spacing="narrow">
            <Stack.Item fill>
              <Action.AutoSavedFilteredSelectControl
                id="meta-site-settings-general-timezone"
                label={t('meta-sites.details.settings.general.timezone.label')}
                info={timezoneOption ? t('meta-sites.details.settings.general.timezone.help', {
                  date: formatDate(new Date(), {
                    format: 'LT',
                    timeZone: timezoneOption.intlValue ?? timezoneOption.value,
                  }),
                  currentTimezone: timezone,
                }) : null}
                options={[{
                  id: 'timezone-select-field',
                  items: filteredTimezones,
                }]}
                name="timezone"
                search={timezoneSearch}
                onSearch={e => this.handleSearchChange(e)}
                onOpen={this.clearSearchInput}
                initialValue={timezone}
                onSubmit={this.handleSubmit}
                getSelectedName={
                  value => filteredTimezones.find(option => value === option.id).name
                }
              />
            </Stack.Item>
            <Stack.Item fill>
              {!timezoneValid && (
                <Information type="warning">
                  {t('meta-sites.details.settings.general.timezone.integrity.error', { timezone })}
                </Information>
              )}
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item previousSpacing={timezoneValid ? 'narrow' : 'default'} fill>
          <Divider noMargin />
        </Stack.Item>
        <Stack.Item fill>
          <Stack spacing="narrow" verticalAlignment="center">
            <Stack.Item fill>
              <Text as="p" weight={600} dataId="Label">
                {t('meta-sites.details.settings.general.currency.label')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              {this.getCurrencyStatus()}
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <HelpLink href="https://help.piwik.pro/support/getting-started/currency-conversion/">
            Currency conversion
          </HelpLink>
        </Stack.Item>
      </Stack>
    );
  }
}

export default connector(withInject<InjectedProps>({
  config: 'config',
  metaSites: 'MetaSitesCrate.metaSites',
})(
  withTranslation(['administration'])(errorBoundary(GeneralTimezoneAndCurrency)),
));
